import React, { useState, useEffect, useRef, useMemo } from "react";
import { socket } from "../socket";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { buildTornWall } from "./tornWall";
import { alertDialog } from "../dialog";

// Références : un mur de fragments d'affiches déchirées. On clique un fragment
// pour proposer ce qu'il représente ; le panneau de droite se remplit au fur et
// à mesure, avec l'avatar de qui a trouvé quoi.
//
// Le serveur n'envoie que le fragment : l'affiche entière n'arrive qu'une fois
// la référence trouvée. Il n'y a donc rien à découvrir en inspectant la page.
//
// Une trouvaille ne change pas le mur : le morceau reste en place et s'éteint,
// pour que les restants sautent aux yeux. C'est l'affiche entière qui passe
// devant l'écran, deux secondes, puis s'efface.
//
// Le mur est bien plus grand que l'écran : on s'y promène (défilement, doigt,
// ou cliquer-glisser à la souris) plutôt que de tout voir en vignettes.
const REVEAL_MS = 2000;
// Au-delà de ce déplacement, un appui devient un glisser et ne choisit pas de tuile.
const DRAG_PX = 6;

// Image du fragment calée sur le rectangle englobant de sa tuile (en « cover »),
// plutôt qu'étalée sur tout le mur dont la tuile ne montrerait qu'une lamelle.
// Unités cqw : 1 % de la largeur du mur, qui garde un rapport 16/9.
function fragmentFit(box) {
  if (!box) return {};
  const [x, y, w, h] = box;
  const top = y * 9 / 16, hw = h * 9 / 16;
  const side = Math.max(w, hw);
  return {
    backgroundSize: `${side}cqw ${side}cqw`,
    backgroundPosition: `${x + (w - side) / 2}cqw ${top + (hw - side) / 2}cqw`,
  };
}

function References({ gameId, player }) {
  const [items, setItems]     = useState([]);
  const [players, setPlayers] = useState([]);
  const [mode, setMode]       = useState("detente");
  const [endsAt, setEndsAt]   = useState(0);
  const [active, setActive]   = useState(null);   // fragment en cours de proposition
  const [guess, setGuess]     = useState("");
  const [shake, setShake]     = useState(null);
  const [lockedUntil, setLock] = useState(0);
  const [now, setNow]         = useState(Date.now());
  const [reveal, setReveal]   = useState(null);   // l'affiche montrée 2 secondes

  const myId = player?.playerId;
  const { playPop, playSuccess, playFail } = useSounds();
  const inputRef = useRef(null);
  const viewRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    const apply = (s) => {
      setItems(s.items); setPlayers(s.players);
      setMode(s.mode); setEndsAt(s.endsAt);
    };
    socket.on("rfStart", apply);
    socket.on("rfState", apply);
    socket.on("rfFound", ({ index, answer, poster, playerId, players }) => {
      (playerId === myId ? playSuccess : playPop)();
      setItems(prev => prev.map((it, i) =>
        i === index ? { ...it, found: true, answer, poster, foundBy: playerId } : it));
      setPlayers(players);
      setActive(a => (a === index ? null : a));
      // Le morceau reste le morceau : c'est l'affiche entière qui passe devant
      // l'écran, le temps de la voir, puis s'efface.
      setReveal({ at: Date.now(), answer, poster, playerId });
    });
    // Une tuile qui a résisté à plusieurs propositions est redécoupée plus
    // largement par le serveur : on remplace son image sur place.
    socket.on("rfEased", ({ index, fragment, eased }) => {
      setItems(prev => prev.map((it, i) => i === index ? { ...it, fragment, eased } : it));
    });
    socket.on("rfWrong", ({ index, lockedFor }) => {
      playFail();
      setShake(index);
      setTimeout(() => setShake(null), 400);
      if (lockedFor > 0) { setNow(Date.now()); setLock(Date.now() + lockedFor); }
    });
    socket.on("rfError", ({ error }) => alertDialog(error));

    socket.emit("rfGetState", { gameId });
    return () => {
      ["rfStart", "rfState", "rfFound", "rfWrong", "rfError", "rfEased"].forEach(e => socket.off(e));
    };
  }, [gameId, myId, playPop, playSuccess, playFail]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { if (active !== null) inputRef.current?.focus(); }, [active]);

  useEffect(() => {
    if (!reveal) return undefined;
    const id = setTimeout(() => setReveal(null), REVEAL_MS);
    return () => clearTimeout(id);
  }, [reveal?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Le mur est recalculé seulement quand le nombre de morceaux change ; la
  // graine vient du code de partie pour que tout le monde voie le même découpage.
  const { shapes, centers, boxes, tears } = useMemo(() => buildTornWall(items.length, gameId), [items.length, gameId]);

  // On arrive au milieu du mur.
  useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    v.scrollLeft = (v.scrollWidth - v.clientWidth) / 2;
    v.scrollTop = (v.scrollHeight - v.clientHeight) / 2;
  }, [items.length]);

  // Le doigt et la molette font défiler nativement ; la souris, elle, glisse.
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const v = viewRef.current;
    drag.current = { x: e.clientX, y: e.clientY, left: v.scrollLeft, top: v.scrollTop, moved: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.up) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moved) {
      if (Math.hypot(dx, dy) < DRAG_PX) return;
      d.moved = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    viewRef.current.scrollLeft = d.left - dx;
    viewRef.current.scrollTop = d.top - dy;
  };
  const onPointerUp = () => { if (drag.current) drag.current.up = true; };
  // Un glisser se termine par un clic : il ne doit pas choisir la tuile d'arrivée.
  const onClickCapture = (e) => {
    if (drag.current?.moved) { e.stopPropagation(); e.preventDefault(); }
    drag.current = null;
  };

  const locked = lockedUntil > now;
  const left = endsAt ? Math.max(0, Math.ceil((endsAt - now) / 1000)) : 0;
  const remaining = items.filter(it => !it.found).length;

  useCountdownTick(left, { active: !!endsAt });

  const send = (e) => {
    e.preventDefault();
    if (active === null || !guess.trim() || locked) return;
    socket.emit("rfGuess", { gameId, index: active, guess });
    setGuess("");
  };

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (items.length === 0) {
    return <div className="References"><p className="rf-waiting">{t("references.waiting")}</p></div>;
  }

  return (
    <div className="References">
      <div className="rf-layout">
        <div className="rf-viewport" ref={viewRef}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onClickCapture={onClickCapture}>
        <div className="rf-wall" style={{ "--rf-span": Math.sqrt(items.length) }}>
          {/* Les tuiles se touchent : la déchirure est ce trait posé dessus,
              d'épaisseur constante sur tout le mur. */}
          <svg className="rf-tears" viewBox="0 0 160 90" preserveAspectRatio="none" aria-hidden="true">
            <path d={tears} />
          </svg>
          {items.map((it, i) => (
            <button key={i} type="button"
              className={`rf-frag${it.found ? " found" : ""}${active === i ? " active" : ""}${shake === i ? " shake" : ""}`}
              style={{
                clipPath: shapes[i] || undefined,
                backgroundImage: `url(${it.fragment})`,
                ...fragmentFit(boxes[i]),
              }}
              disabled={it.found}
              onClick={() => setActive(active === i ? null : i)}>
              {it.found && (
                <span className="rf-frag-name"
                  style={{ left: `${centers[i]?.[0] ?? 50}%`, top: `${centers[i]?.[1] ?? 50}%` }}>
                  {it.answer}
                </span>
              )}
            </button>
          ))}
        </div>
        </div>

        <aside className="rf-panel">
          <div className="rf-panel-head">
            {endsAt > 0 && <span className="rf-timer">{mmss(left)}</span>}
            <span className="rf-remaining">{t("references.remaining", { n: remaining })}</span>
          </div>
          <ol className="rf-list">
            {items.map((it, i) => {
              // Trouvée avant une mise en pause par quelqu'un d'absent : son pseudo suffit.
              const finder = players.find(p => p.playerId === it.foundBy)
                || (it.foundByPseudo ? { playerId: it.foundBy, pseudo: it.foundByPseudo } : null);
              return (
                <li key={i} className={`rf-row${it.found ? " found" : ""}`}>
                  <span className="rf-row-num">{i + 1}</span>
                  <span className="rf-row-name">{it.found ? it.answer : t("references.unknown")}</span>
                  {finder && <PlayerAvatar player={finder} size={22} />}
                </li>
              );
            })}
          </ol>
          <div className="rf-scores">
            {[...players].sort((a, b) => b.found - a.found).map(p => (
              <div key={p.playerId} style={bannerStyle(p)}
                className={`rf-score${p.playerId === myId ? " me" : ""}`}>
                <PlayerAvatar player={p} size={22} />
                <PlayerName player={p} className="rf-score-name" />
                <span className="rf-score-n">{p.found}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {active !== null && (
        <form className="rf-guess-row" onSubmit={send}>
          <input ref={inputRef} className="rf-guess" value={guess} autoComplete="off"
            disabled={locked}
            onChange={e => setGuess(e.target.value)}
            placeholder={locked
              ? t("references.lockedFor", { s: Math.ceil((lockedUntil - now) / 1000) })
              : t("references.guessPlaceholder", { n: active + 1 })} />
          <button type="submit" className="btn btn-primary rf-send" disabled={!guess.trim() || locked}>
            {t("references.send")}
          </button>
          <button type="button" className="rf-cancel" onClick={() => { setActive(null); setGuess(""); }}>
            {t("common.cancel")}
          </button>
        </form>
      )}
      {reveal && (
        <div className="rf-reveal" aria-hidden="true">
          <div className="rf-reveal-card">
            <img className="rf-reveal-poster" src={reveal.poster} alt="" />
            <span className="rf-reveal-name">{reveal.answer}</span>
            {(() => {
              const by = players.find(p => p.playerId === reveal.playerId);
              return by ? <span className="rf-reveal-by">{t("references.revealBy", { name: by.pseudo })}</span> : null;
            })()}
          </div>
        </div>
      )}

      {active === null && (
        <p className="rf-hint">
          {mode === "competitif" ? t("references.hintVersus") : t("references.hintChill")}
        </p>
      )}
    </div>
  );
}

export default References;
